import LangEnum from '../../../../lang/LangEnum';
import snackbarStore from '../../../../stores/Snackbar.store';
import { useTranslation } from '../../../translation/TranslationContext';
import { useOrderCalculateContext } from '../../contexts/OrderCalculate.context';
import { useGetOrderTotals } from '../../hooks/useOrderTotals/order-totals.queries';
import { ordersRepository } from '../../repositories/ordersRepository/orders.repository';
import {
  ValidateOrUpdateConditionsResponse,
  ValidateOrUpdatePricesResponse,
} from '../types/useUpdateOrder.types';
import useUpdateOrderInvalidations from './useUpdateOrderInvalidations';

const useUpdateOrder = () => {
  const { handleMessage } = snackbarStore;
  const { translate } = useTranslation();
  const { calculateSummary } = useOrderCalculateContext();
  const { invalidateCommonQueries, invalidateOrderItemsGrid, invalidateOrderConditions } =
    useUpdateOrderInvalidations();

  const { refetch: refetchOrderTotals } = useGetOrderTotals();

  const validateOrUpdatePrices = async (
    orderId: string,
    forceUpdate: boolean,
  ): Promise<ValidateOrUpdatePricesResponse> => {
    const response = await ordersRepository(orderId).validateOrUpdateOrderItemPrices(forceUpdate);
    if (!response.ok || !response.data) {
      return { hasError: true };
    }
    const { itemsToUpdate, updatedItems } = response.data;
    if (itemsToUpdate.length > 0) {
      return { hasPricesToUpdate: true };
    }
    if (updatedItems.length > 0) {
      invalidateOrderItemsGrid(orderId, updatedItems);
      refetchOrderTotals();
      return { hasUpdatedPrices: true };
    }
    return {};
  };

  const validateOrUpdateConditions = async (
    orderId: string,
    forceUpdate: boolean,
  ): Promise<ValidateOrUpdateConditionsResponse> => {
    const response = await ordersRepository(orderId).validateOrUpdateOrderConditions(forceUpdate);
    if (!response.ok || !response.data) {
      return { hasError: true };
    }
    const hasConditionsToUpdate = response.data.conditionIdsToUpdate.length > 0;
    const hasUpdatedConditions = response.data.updatedConditionIds.length > 0;
    if (hasConditionsToUpdate) {
      return { hasConditionsToUpdate: true };
    }
    if (hasUpdatedConditions) {
      invalidateOrderConditions(orderId);
      return { hasUpdatedConditions: true };
    }
    return {};
  };

  const showErrorFeedback = () => {
    return handleMessage({
      type: 'error',
      message: translate(LangEnum.FORCE_UPDATE_ORDER_ERROR_FEEDBACK),
    });
  };

  const validateOrUpdateOrder = async (
    orderId: string,
    forceUpdate: boolean,
  ): Promise<{ isOrderOutdated: boolean; hasUpdatedOrder: boolean }> => {
    const [res1, res2] = await Promise.all([
      validateOrUpdatePrices(orderId, forceUpdate),
      validateOrUpdateConditions(orderId, forceUpdate),
    ]);

    if (res1.hasError || res2.hasError) {
      showErrorFeedback();
      return { isOrderOutdated: false, hasUpdatedOrder: false };
    }

    const isOrderOutdated = !!res1.hasPricesToUpdate || !!res2.hasConditionsToUpdate;
    const hasUpdatedOrder = !!res1.hasUpdatedPrices || !!res2.hasUpdatedConditions;

    if (hasUpdatedOrder) {
      invalidateCommonQueries(orderId);
      calculateSummary();
    }

    return {
      isOrderOutdated,
      hasUpdatedOrder,
    };
  };

  return {
    validateOrUpdateOrder,
    validateOrUpdatePrices,
    validateOrUpdateConditions,
  };
};

export default useUpdateOrder;
